import React, { useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getApiErrorMessage } from "../../store/api/baseApi";
import {
  useSendCustomNotificationMutation,
  type SendCustomNotificationPayload,
} from "../../store/api/notificationsApi";
import "./admin.css";

// ── Preset templates ────────────────────────────────────────────────────────
const TEMPLATES: { label: string; icon: string; subject: string; message: string }[] = [
  {
    label: "Enquiry Confirmation",
    icon: "bi bi-check-circle-fill",
    subject: "Your Enquiry is Confirmed – Dialin24",
    message:
      "Thank you for reaching out to Dialin24 Construction Services.\n\nWe have received your enquiry and our team will get back to you within 24 hours with a personalized consultation.\n\nFor immediate assistance, please call us at +91 81828 33009.\n\nWarm regards,\nDialin24 Construction Team",
  },
  {
    label: "Follow-Up",
    icon: "bi bi-arrow-clockwise",
    subject: "Following Up on Your Enquiry – Dialin24",
    message:
      "We wanted to follow up on your recent enquiry with Dialin24 Construction Services.\n\nOur consultant is ready to discuss your project and answer any questions you may have. Please feel free to reach us at +91 81828 33009 or simply reply to this message.\n\nWe look forward to hearing from you!\n\nBest regards,\nDialin24 Construction Team",
  },
  {
    label: "Appointment Reminder",
    icon: "bi bi-calendar-check-fill",
    subject: "Appointment Reminder – Dialin24 Site Visit",
    message:
      "This is a friendly reminder about your upcoming consultation/site visit with Dialin24 Construction Services.\n\nPlease ensure you are available at the agreed time. If you need to reschedule, contact us at +91 81828 33009.\n\nThank you for choosing Dialin24!\n\nBest regards,\nDialin24 Construction Team",
  },
  {
    label: "Project Update",
    icon: "bi bi-kanban-fill",
    subject: "Project Update from Dialin24",
    message:
      "We have an update regarding your ongoing project with Dialin24 Construction Services.\n\n[Add your project update here]\n\nIf you have any questions, please don't hesitate to contact us at +91 81828 33009.\n\nBest regards,\nDialin24 Construction Team",
  },
];

const CHANNEL_META = [
  { key: "sendEmail",    icon: "bi bi-envelope-fill",  label: "Email",    color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  { key: "sendSms",      icon: "bi bi-phone-fill",     label: "SMS",      color: "#0d9488", bg: "#f0fdfa", border: "#99f6e4" },
  { key: "sendWhatsApp", icon: "bi bi-whatsapp",       label: "WhatsApp", color: "#16a34a", bg: "#f0fdf4", border: "#86efac" },
] as const;

type ChannelKey = "sendEmail" | "sendSms" | "sendWhatsApp";

interface FormState {
  toName: string;
  toEmail: string;
  toPhone: string;
  subject: string;
  message: string;
  sendEmail: boolean;
  sendSms: boolean;
  sendWhatsApp: boolean;
}

const defaultForm: FormState = {
  toName: "",
  toEmail: "",
  toPhone: "",
  subject: "",
  message: "",
  sendEmail: false,
  sendSms: false,
  sendWhatsApp: false,
};

const NotificationComposer = () => {
  const [sendCustom, { isLoading }] = useSendCustomNotificationMutation();
  const [form, setForm] = useState<FormState>(defaultForm);
  const [activeTemplate, setActiveTemplate] = useState<number | null>(null);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const set = (field: keyof FormState, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const applyTemplate = (idx: number) => {
    const t = TEMPLATES[idx];
    setForm((prev) => ({ ...prev, subject: t.subject, message: t.message }));
    setActiveTemplate(idx);
    setResult(null);
  };

  const needsEmail = form.sendEmail;
  const needsPhone = form.sendSms || form.sendWhatsApp;
  const emailMissing = needsEmail && !form.toEmail.trim();
  const phoneMissing = needsPhone && !form.toPhone.trim();
  const noChannel   = !form.sendEmail && !form.sendSms && !form.sendWhatsApp;
  const msgMissing  = !form.message.trim();
  const canSend     = !noChannel && !emailMissing && !phoneMissing && !msgMissing && !isLoading;

  const handleSend = async () => {
    if (!canSend) return;
    setResult(null);
    const payload: SendCustomNotificationPayload = {
      toName:      form.toName.trim() || undefined,
      toEmail:     form.toEmail.trim() || undefined,
      toPhone:     form.toPhone.trim() || undefined,
      subject:     form.subject.trim() || "Message from Dialin24",
      message:     form.message.trim(),
      sendEmail:   form.sendEmail,
      sendSms:     form.sendSms,
      sendWhatsApp: form.sendWhatsApp,
    };
    try {
      const res = await sendCustom(payload).unwrap();
      setResult({ ok: true, msg: res.message || "Notification sent successfully." });
      setForm(defaultForm);
      setActiveTemplate(null);
    } catch (err) {
      setResult({ ok: false, msg: getApiErrorMessage(err as FetchBaseQueryError) });
    }
  };

  // ── Preview ───────────────────────────────────────────────────────────────
  const previewName = form.toName.trim() || "Customer";
  const previewMsg  = form.message.trim().replace(/\n/g, "<br/>");

  return (
    <section className='admin-page'>
      <div className='container-fluid px-0'>
        <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a" }}>

          {/* ── Gradient header ── */}
          <div style={{ background: "linear-gradient(135deg,#0c2420,#134e4a,#0d9488)", padding: "12px 22px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className='bi bi-send-fill' style={{ color: "#fff", fontSize: 14 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.92rem", color: "#fff", lineHeight: 1.2 }}>Notification Composer</div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.7)" }}>Send custom Email, SMS or WhatsApp to any recipient</div>
              </div>
            </div>
          </div>

          <div style={{ padding: "22px 24px" }}>
            <div className='row g-4'>

              {/* ── LEFT: Form ── */}
              <div className='col-lg-7'>

                {/* Quick Templates */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#64748b", marginBottom: 10 }}>
                    Quick Templates
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {TEMPLATES.map((t, idx) => (
                      <button
                        key={idx}
                        type='button'
                        title={t.label}
                        onClick={() => applyTemplate(idx)}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          padding: "6px 12px", borderRadius: 8, fontSize: "0.78rem", fontWeight: 600,
                          cursor: "pointer", transition: "all 0.15s",
                          background: activeTemplate === idx ? "linear-gradient(135deg,#0d9488,#0f766e)" : "#f8fafc",
                          color: activeTemplate === idx ? "#fff" : "#334155",
                          border: activeTemplate === idx ? "none" : "1.5px solid #e2e8f0",
                        }}
                      >
                        <i className={t.icon} style={{ fontSize: "0.75rem" }} />
                        {t.label}
                      </button>
                    ))}
                    <button
                      type='button'
                      title='Clear template'
                      onClick={() => { setForm(defaultForm); setActiveTemplate(null); setResult(null); }}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, fontSize: "0.78rem", fontWeight: 600, cursor: "pointer", background: "#f8fafc", color: "#94a3b8", border: "1.5px solid #e2e8f0" }}
                    >
                      <i className='bi bi-x-circle' style={{ fontSize: "0.75rem" }} />
                      Clear
                    </button>
                  </div>
                </div>

                {/* Recipient */}
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 18px", marginBottom: 16 }}>
                  <div style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#64748b", marginBottom: 12 }}>
                    Recipient
                  </div>
                  <div className='row g-3'>
                    <div className='col-md-4'>
                      <label className='form-label mb-1' style={{ fontSize: "0.78rem", fontWeight: 600, color: "#334155" }}>Name <span style={{ color: "#94a3b8", fontWeight: 400 }}>(optional)</span></label>
                      <div className='input-group input-group-sm'>
                        <span className='input-group-text' style={{ background: "#eef2ff", borderColor: "#c7d2fe", color: "#6366f1" }}><i className='bi bi-person-fill' /></span>
                        <input className='form-control form-control-sm' placeholder='e.g. Rahul Sharma' title='Recipient name' value={form.toName} onChange={(e) => set("toName", e.target.value)} />
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <label className='form-label mb-1' style={{ fontSize: "0.78rem", fontWeight: 600, color: "#334155" }}>Email Address {form.sendEmail && <span style={{ color: "#dc2626" }}>*</span>}</label>
                      <div className='input-group input-group-sm'>
                        <span className='input-group-text' style={{ background: "#fef3c7", borderColor: "#fcd34d", color: "#d97706" }}><i className='bi bi-envelope-fill' /></span>
                        <input className='form-control form-control-sm' placeholder='email@example.com' title='Recipient email' type='email' value={form.toEmail} onChange={(e) => set("toEmail", e.target.value)} style={{ borderColor: emailMissing ? "#fca5a5" : undefined }} />
                      </div>
                      {emailMissing && <div style={{ fontSize: "0.7rem", color: "#dc2626", marginTop: 3 }}>Required for Email channel</div>}
                    </div>
                    <div className='col-md-4'>
                      <label className='form-label mb-1' style={{ fontSize: "0.78rem", fontWeight: 600, color: "#334155" }}>Phone Number {(form.sendSms || form.sendWhatsApp) && <span style={{ color: "#dc2626" }}>*</span>}</label>
                      <div className='input-group input-group-sm'>
                        <span className='input-group-text' style={{ background: "#d1fae5", borderColor: "#a7f3d0", color: "#059669" }}><i className='bi bi-telephone-fill' /></span>
                        <input className='form-control form-control-sm' placeholder='+91 XXXXX XXXXX' title='Recipient phone number' type='tel' value={form.toPhone} onChange={(e) => set("toPhone", e.target.value)} style={{ borderColor: phoneMissing ? "#fca5a5" : undefined }} />
                      </div>
                      {phoneMissing && <div style={{ fontSize: "0.7rem", color: "#dc2626", marginTop: 3 }}>Required for SMS / WhatsApp</div>}
                    </div>
                  </div>
                </div>

                {/* Channels */}
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 18px", marginBottom: 16 }}>
                  <div style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#64748b", marginBottom: 12 }}>
                    Send Via
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {CHANNEL_META.map(({ key, icon, label, color, bg, border }) => {
                      const active = form[key as ChannelKey];
                      return (
                        <button
                          key={key}
                          type='button'
                          title={`Toggle ${label}`}
                          onClick={() => set(key as ChannelKey, !active)}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 8,
                            padding: "8px 14px", borderRadius: 10, cursor: "pointer", transition: "all 0.15s",
                            background: active ? bg : "#fff",
                            border: `1.5px solid ${active ? color : "#e2e8f0"}`,
                            fontWeight: 600, fontSize: "0.82rem",
                            color: active ? color : "#64748b",
                          }}
                        >
                          <i className={icon} style={{ fontSize: "0.9rem" }} />
                          {label}
                          {active && <i className='bi bi-check-circle-fill' style={{ fontSize: "0.75rem", color }} />}
                        </button>
                      );
                    })}
                  </div>
                  {noChannel && <div style={{ fontSize: "0.72rem", color: "#dc2626", marginTop: 8 }}>Select at least one channel</div>}
                </div>

                {/* Message */}
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 18px", marginBottom: 20 }}>
                  <div style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#64748b", marginBottom: 12 }}>
                    Message
                  </div>
                  {form.sendEmail && (
                    <div style={{ marginBottom: 10 }}>
                      <label className='form-label mb-1' style={{ fontSize: "0.78rem", fontWeight: 600, color: "#334155" }}>Subject</label>
                      <input
                        className='form-control form-control-sm'
                        placeholder='Email subject line'
                        title='Email subject'
                        value={form.subject}
                        onChange={(e) => set("subject", e.target.value)}
                      />
                    </div>
                  )}
                  <div>
                    <label className='form-label mb-1' style={{ fontSize: "0.78rem", fontWeight: 600, color: "#334155" }}>
                      Message Body <span style={{ color: "#dc2626" }}>*</span>
                      <span style={{ color: "#94a3b8", fontWeight: 400, marginLeft: 6 }}>({form.message.length} chars)</span>
                      {(form.sendSms || form.sendWhatsApp) && form.message.length > 0 && (
                        <span style={{ marginLeft: 6, fontSize: "0.68rem", color: form.message.length > 160 ? "#f59e0b" : "#0d9488", fontWeight: 700 }}>
                          {form.message.length > 160 ? `${Math.ceil(form.message.length / 160)} SMS segments` : "1 SMS segment"}
                        </span>
                      )}
                    </label>
                    <textarea
                      className='form-control form-control-sm'
                      rows={7}
                      placeholder='Type your message here…'
                      title='Message body'
                      value={form.message}
                      onChange={(e) => { set("message", e.target.value); setActiveTemplate(null); }}
                      style={{ resize: "vertical", fontFamily: "inherit", lineHeight: 1.6 }}
                    />
                    {msgMissing && <div style={{ fontSize: "0.7rem", color: "#dc2626", marginTop: 3 }}>Message body is required</div>}
                  </div>
                </div>

                {/* Result */}
                {result && (
                  <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 10, background: result.ok ? "#f0fdf4" : "#fef2f2", border: `1px solid ${result.ok ? "#86efac" : "#fca5a5"}`, display: "flex", alignItems: "center", gap: 8 }}>
                    <i className={result.ok ? "bi bi-check-circle-fill" : "bi bi-x-circle-fill"} style={{ color: result.ok ? "#16a34a" : "#dc2626", fontSize: "1rem" }} />
                    <span style={{ fontSize: "0.84rem", color: result.ok ? "#15803d" : "#dc2626", fontWeight: 600 }}>{result.msg}</span>
                  </div>
                )}

                {/* Send button */}
                <button
                  type='button'
                  disabled={!canSend}
                  onClick={handleSend}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    background: canSend ? "linear-gradient(135deg,#0d9488,#0f766e)" : "#e2e8f0",
                    color: canSend ? "#fff" : "#94a3b8",
                    border: "none", borderRadius: 10, padding: "10px 24px",
                    fontWeight: 700, fontSize: "0.9rem", cursor: canSend ? "pointer" : "not-allowed",
                    boxShadow: canSend ? "0 4px 14px #0d948844" : "none",
                    transition: "all 0.2s",
                  }}
                >
                  {isLoading
                    ? <><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' /> Sending…</>
                    : <><i className='bi bi-send-fill' /> Send Notification</>}
                </button>
              </div>

              {/* ── RIGHT: Live preview ── */}
              <div className='col-lg-5'>
                <div style={{ position: "sticky", top: 16 }}>
                  <div style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#64748b", marginBottom: 10 }}>
                    Email Preview
                  </div>
                  <div style={{ border: "1.5px solid #e2e8f0", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 12px #00000008" }}>
                    {/* Mock email client header */}
                    <div style={{ background: "#f1f5f9", padding: "8px 14px", display: "flex", alignItems: "center", gap: 6, borderBottom: "1px solid #e2e8f0" }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#fca5a5" }} />
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#fcd34d" }} />
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#86efac" }} />
                      <span style={{ fontSize: "0.68rem", color: "#94a3b8", marginLeft: 6 }}>Email Preview</span>
                    </div>
                    {/* Preview body */}
                    <div style={{ padding: 0, background: "#f0f2f8" }}>
                      {/* Mini email */}
                      <div style={{ margin: "14px 14px 14px", background: "#fff", borderRadius: 10, overflow: "hidden", boxShadow: "0 2px 8px #00000010" }}>
                        <div style={{ background: "linear-gradient(135deg,#0c2420,#134e4a,#0d9488)", padding: "12px 16px" }}>
                          <div style={{ color: "#fff", fontWeight: 800, fontSize: "0.82rem" }}>Dialin24 Construction</div>
                          <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.65rem" }}>Construction Management Services</div>
                        </div>
                        <div style={{ padding: "12px 16px" }}>
                          {form.subject && <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#0f172a", marginBottom: 4 }}>{form.subject}</div>}
                          <div style={{ fontSize: "0.72rem", color: "#64748b", marginBottom: 10 }}>Hello {previewName},</div>
                          <div
                            style={{ fontSize: "0.72rem", color: "#334155", lineHeight: 1.7, background: "#f8fafc", borderRadius: 6, padding: "8px 10px", minHeight: 60, whiteSpace: "pre-wrap" }}
                          >
                            {form.message || <span style={{ color: "#94a3b8" }}>Your message will appear here…</span>}
                          </div>
                          <div style={{ fontSize: "0.65rem", color: "#94a3b8", marginTop: 10 }}>
                            For queries call <span style={{ color: "#0d9488" }}>+91 81828 33009</span>
                          </div>
                        </div>
                        <div style={{ background: "#f8fafc", padding: "8px 16px", borderTop: "1px solid #f1f5f9" }}>
                          <div style={{ fontSize: "0.62rem", color: "#94a3b8" }}>© Dialin24 Construction Services · Lucknow &amp; Barabanki, UP</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Channel summary */}
                  <div style={{ marginTop: 14, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px" }}>
                    <div style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#64748b", marginBottom: 8 }}>Will be sent via</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {CHANNEL_META.map(({ key, icon, label, color }) => {
                        const active = form[key as ChannelKey];
                        return (
                          <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, opacity: active ? 1 : 0.35 }}>
                            <i className={icon} style={{ color: active ? color : "#94a3b8", fontSize: "0.82rem", width: 16 }} />
                            <span style={{ fontSize: "0.78rem", color: active ? "#0f172a" : "#94a3b8", fontWeight: active ? 600 : 400 }}>{label}</span>
                            {active && (
                              <span style={{ marginLeft: "auto", fontSize: "0.65rem", color, fontWeight: 700 }}>
                                {key === "sendEmail" && (form.toEmail || <em style={{ color: "#fca5a5" }}>no email</em>)}
                                {(key === "sendSms" || key === "sendWhatsApp") && (form.toPhone || <em style={{ color: "#fca5a5" }}>no phone</em>)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NotificationComposer;
